import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import FilterBar, { FilterSummary, defaultFilters, type FilterState } from '@/components/feature/FilterBar';
import MobileFilter from '@/components/feature/MobileFilter';
import Pager from '@/components/feature/Pager';
import { CATEGORY_GENRES, mediaList, type MediaItem } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const categoryCopy: Record<string, { desc: string; keywords: string }> = {
  电影: { desc: '院线新片、影展佳作与经典回顾，按题材、地区与年份整理，方便你快速挑选今晚要看的那一部。', keywords: '电影推荐,最新电影,电影资讯,院线新片' },
  电视剧: { desc: '古装长剧、都市现实与悬疑短剧，追更不漏集，先用筛选缩小范围再决定要不要开追。', keywords: '电视剧推荐,最新电视剧,剧集资讯,热播剧' },
  动漫: { desc: '国产动画与日系作品，热血、治愈、奇幻各成一路，按集数长度和完成度做区分。', keywords: '动漫推荐,新番,国产动画,治愈动漫' },
  综艺: { desc: '真人秀、音乐竞技与美食访谈，挑选适合下饭的轻松节目，也收着几档值得坐下来看的慢综艺。', keywords: '综艺推荐,真人秀,音乐综艺,美食综艺' },
  纪录片: { desc: '人文、自然与美食纪录，节奏慢、信息密度高，适合在周末留出完整的两小时。', keywords: '纪录片推荐,自然纪录,人文纪录,高分纪录片' },
};

export function applyFilters(list: MediaItem[], filters: FilterState): MediaItem[] {
  const filtered = list.filter((item) => {
    if (filters.genre !== '全部' && !item.genres.includes(filters.genre)) return false;
    if (filters.region !== '全部' && item.region !== filters.region) return false;
    if (filters.year !== '全部' && String(item.year) !== filters.year) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    if (filters.sort === 'rating') return b.rating - a.rating;
    if (filters.sort === 'hot') return b.heat - a.heat;
    return b.year - a.year;
  });
}

export default function CategoryPage() {
  const { type = '电影' } = useParams();
  const categoryType = decodeURIComponent(type);
  const copy = categoryCopy[categoryType] || { desc: '按类型浏览影视作品，支持题材、地区与年份筛选。', keywords: '影视分类,影视推荐' };
  const genres = CATEGORY_GENRES[categoryType] || ['全部'];

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [shown, setShown] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilters(defaultFilters);
    setShown(PAGE_SIZE);
  }, [categoryType]);

  usePageMeta({
    title: `${categoryType}分类 - 菠萝蜜网影视内容`,
    description: `${categoryType}分类：${copy.desc}`,
    keywords: copy.keywords,
    path: `/category/${encodeURIComponent(categoryType)}`,
  });

  const baseList = mediaList.filter((item) => item.type === categoryType);
  const results = useMemo(() => applyFilters(baseList, filters), [baseList, filters]);

  const patchFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setShown(PAGE_SIZE);
  };

  const spotlight = [...baseList].sort((a, b) => b.rating - a.rating)[0];

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb items={[{ label: `${categoryType}分类` }]} />

        <section className="relative mb-5 overflow-hidden rounded-xl bg-secondary-950">
          <div className="relative h-[168px] w-full md:h-[200px]">
            {spotlight && (
              <img src={spotlight.backdrop} alt={`${categoryType}分类头图`} title={`${categoryType}分类推荐`} className="h-full w-full object-cover opacity-70" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/70 to-transparent" />
          </div>
          <div className="absolute inset-0 flex flex-col justify-center p-5 md:p-8">
            <p className="text-[11px] tracking-[0.2em] text-accent-400">CATEGORY · {baseList.length} 部作品</p>
            <h1 className="mt-1.5 font-heading text-2xl font-bold text-background-50 md:text-3xl">{categoryType}分类</h1>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-background-200/80 md:text-[13px]">{copy.desc}</p>
          </div>
        </section>

        <div className="hidden lg:block">
          <FilterBar genres={genres} filters={filters} onChange={patchFilters} />
        </div>
        <div className="mt-3 lg:hidden">
          <MobileFilter genres={genres} filters={filters} onChange={patchFilters} resultCount={results.length} />
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <FilterSummary filters={filters} onReset={() => patchFilters(defaultFilters)} />
          <p className="ml-auto text-[11px] text-foreground-500">共 {results.length} 部 · 当前显示 {Math.min(shown, results.length)} 部</p>
        </div>

        {results.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-xl" />
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">没有符合条件的作品</h3>
            <p className="mt-2 text-xs text-foreground-500">试试放宽筛选条件，或浏览其他{categoryType}作品。</p>
            <button
              type="button"
              onClick={() => patchFilters(defaultFilters)}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
              {results.slice(0, shown).map((item, index) => (
                <MediaCard key={item.id} id={item.id} index={index} />
              ))}
            </div>
            <Pager total={results.length} shown={Math.min(shown, results.length)} onChange={setShown} />
          </>
        )}
      </PortalShell>
    </SiteLayout>
  );
}

export { PAGE_SIZE };